import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import React, { useCallback, useState } from 'react';
import { Button, Col, Dropdown, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaAngleDown } from 'react-icons/fa';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';

import { uiActions } from '../../../actions';
import LoadingImage from '../../../components/general/LoadingImage';
import { parseCSV } from '../../../helpers/csv.helper';
import useDispatch from '../../../hooks/useDispatch';
import { useGeneralErrorHandler } from '../../../hooks/useGeneralErrorHandler';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { useUser } from '../../../hooks/users';
import {
  CCASSShareholdingDetail,
  CCASSSummary,
} from '../../../py-generated/datalouder/ccass/types';
import { ccassService, dailypriceService } from '../../../services';
import CCASSShareholdingCharts from './CCASSShareholdingCharts';
import CCASSShareholdingTable from './CCASSShareholdingTable';
const processCCASSDataDetails = (data: any): CCASSShareholdingDetail[] => {
  return data ? parseCSV<any>(data) : [];
};

interface Props {
  queryDataSummary: CCASSSummary;
  compareDataSummary?: CCASSSummary;
  date: string;
  compareDate: string;
  // TODO: revisit if both picker should be optional.
  compareDatePicker?: (id: string) => React.JSX.Element;
  datePicker?: () => React.JSX.Element;
}
interface ShareholdingData {
  date: string;
  stockCode: string;
  participantId: string;
  shareholding: number;
}

const Style = styled.span`
  .dropdown-toggle {
    border-radius: 100px;
    border: 0px;
    background: var(--Black-0, #f2f2f2);
    color: var(--Black-400, #303030);
    width: 100px;
    height: 24px;
  }
  .text-right{
    text-align: right;
    color: #000;
    font-size:16px;
    font-style: normal;
    font-weight: 600;
    line-height: 120%
    padding-top: 42px;
    min-width: 220px;
    padding: 2px 2px 2px 8px;
  }
  @media only screen and (max-width: 1200px) {
    .fa-chevron-left {
      display: none;
    }
    .fa-chevron-right {
      display: none;
    }
    .input {
      width: 100%;
    }
    .form-group{
      max-width: 30vw;
    }
  }
  @media only screen and (max-width: 768px) {
    .form-group{
      max-width: calc(35vw - 8px);
    }
  }
  @media only screen and (max-width: 576px) {
    .form-group{
      max-width: calc(50% - 8px);
    }
  }
  @media only screen and (max-width: 480px) {
    .form-group{
      max-width: 200px;
    }
  }
`;

const focusOnCompareDatePicker = () => {
  const compareDateInput = document.querySelector(
    '#compareDate-details'
  ) as HTMLInputElement;
  compareDateInput.focus();
};

const subtractDaysFromString = (dateString: string, days: number) => {
  const year = Number.parseInt(dateString.slice(0, 4));
  const month = Number.parseInt(dateString.slice(4, 6)) - 1;
  const day = Number.parseInt(dateString.slice(6, 8));

  const date = new Date(year, month, day);
  date.setDate(date.getDate() - days);

  return `${String(date.getFullYear())}${String(date.getMonth() + 1).padStart(
    2,
    '0'
  )}${String(date.getDate()).padStart(2, '0')}`;
};

const isIntWithinRange = (
  numberToCheck: number,
  number1: number,
  number2: number
) => {
  const minValue = Math.min(number1, number2);
  const maxValue = Math.max(number1, number2);
  return numberToCheck > minValue && numberToCheck <= maxValue;
};

const getClosetIndexByDate = (dates: string[], targetDate: string): number => {
  let closestIndex = -1;
  let closestDiff = Number.POSITIVE_INFINITY;
  for (const [i, currentDate] of dates.entries()) {
    const diff = Number(targetDate) - Number(currentDate);
    if (diff < closestDiff) {
      closestDiff = diff;
      closestIndex = i;
    }
  }
  return closestIndex;
};

const calculateAverages = (
  totalBuy,
  totalBuyShare,
  totalSell,
  totalSellShare
) => {
  const avgBuy = totalBuyShare === 0 ? 0 : totalBuy / totalBuyShare;
  const avgSell = totalSellShare === 0 ? 0 : totalSell / totalSellShare;
  const avgTrade =
    totalBuyShare - totalSellShare === 0
      ? 0
      : (totalBuy - totalSell) / (totalBuyShare - totalSellShare);

  return {
    avgBuy: Number(avgBuy.toFixed(2)),
    avgSell: Number(avgSell.toFixed(2)),
    avgTrade: Number.isNaN(avgTrade) ? 0 : Number(avgTrade.toFixed(2)),
  };
};
const COMPARE_DAY_DELTA_OPTIONS = [5, 10, 50, 150, 250];
const CCASSDetails: React.FC<Props> = ({
  queryDataSummary,
  compareDataSummary,
  date,
  compareDate,
  compareDatePicker,
  datePicker,
}) => {
  const { ref, inView } = useInView();
  const isTablet = useMediaQuery('(max-width: 1200px  )');
  const { t } = useTranslation();
  const { data, isFetching, error } = useQuery(
    [
      'queryCCASSDetail',
      queryDataSummary.ticker,
      queryDataSummary.date,
      compareDataSummary?.date,
    ],
    () =>
      ccassService.queryDetails(
        queryDataSummary.ticker,
        queryDataSummary.date,
        compareDataSummary.date
      ),
    { enabled: inView }
  );
  useGeneralErrorHandler(error);

  const queryDetails = data && processCCASSDataDetails(data.queryDate);
  const compareDetails = data && processCCASSDataDetails(data.compareData);
  let dayAfter = 1;
  if (compareDate !== undefined) {
    dayAfter = moment(date).diff(compareDate, 'days');
  }
  const fetchHistoryShares = useCallback(async () => {
    let outputDict = {};
    await ccassService
      .participantHistory(
        undefined,
        `hk.${Number.parseInt(queryDataSummary.ticker)}`
      )
      .then(({ shareholding }) => {
        const allHistory = parseCSV<ShareholdingData>(shareholding);
        const shares = {};
        const compareDate = subtractDaysFromString(
          compareDataSummary.date.toString(),
          60
        );
        for (const item of allHistory) {
          if (item.date > queryDataSummary.date || item.date < compareDate) {
            continue;
          }
          for (const key in item) {
            if (key !== 'date' && item[key] !== null && item[key] !== '') {
              if (!shares[key]) {
                shares[key] = {};
              }
              shares[key][item.date] = item[key];
            }
          }
        }
        outputDict = shares;
      });
    return outputDict;
  }, [queryDataSummary.ticker, queryDataSummary.date, compareDataSummary.date]);

  const fetchVWAP = useCallback(async () => {
    let lastDate = '';
    let data = [];
    const timeSeriesQueryData = `olhcv:HK#${queryDataSummary.ticker}`;
    do {
      const timeSeriesData = await dailypriceService.timeSeries(
        undefined,
        [timeSeriesQueryData],
        'DAY',
        lastDate
      );
      if (timeSeriesData.data.length === 0) {
        break;
      }
      lastDate = timeSeriesData.data[0].dt;
      data = [...timeSeriesData.data, ...data];
    } while (
      lastDate > compareDataSummary.date ||
      lastDate > subtractDaysFromString(queryDataSummary.date, 400)
    );
    const newVWAP = {};
    for (const item of data) {
      newVWAP[item.dt] =
        item[timeSeriesQueryData].t / item[timeSeriesQueryData].v;
    }
    return newVWAP;
  }, [compareDataSummary.date, queryDataSummary.ticker, queryDataSummary.date]);

  const fetchNotInCCASSHistory = useCallback(async () => {
    const historySummary = await ccassService.summaryHistory(
      undefined,
      `hk.${Number.parseInt(queryDataSummary.ticker)}`
    );

    const noCCASSShares = {};
    for (const item of historySummary) {
      const noCCASS = item.issued_shares - item.total_ccass;
      noCCASSShares[item.Date] = noCCASS;
    }

    return noCCASSShares;
  }, [queryDataSummary.ticker]);
  const [tradingDays, setTradingDays] = useState([] as string[]);
  const getShareholdingAndVwap = useCallback(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    async (queryDate: string, compareDate: string) => {
      const vwapData = await fetchVWAP();
      const historyShares = await fetchHistoryShares();
      const historySummary = await fetchNotInCCASSHistory();
      historyShares['NOTINCCASS'] = historySummary;
      const priceDate = Object.keys(vwapData);
      setTradingDays(priceDate);
      const participantKeys = Object.keys(historyShares);
      const ccassCostData = {};
      let totalChangeAmount = 0;
      if (queryDate === compareDate) {
        return ccassCostData;
      }
      const sharesChange = {};
      for (const date of priceDate) {
        if (
          isIntWithinRange(Number(date), Number(queryDate), Number(compareDate))
        ) {
          const previousDate = priceDate[priceDate.indexOf(date) - 1];
          for (const participant of participantKeys) {
            let shareChange = 0;
            if (
              historyShares[participant][date] === undefined &&
              historyShares[participant][previousDate] !== undefined
            ) {
              shareChange = -historyShares[participant][previousDate];
            } else if (
              previousDate === undefined ||
              (historyShares[participant][previousDate] === undefined &&
                historyShares[participant][date] !== undefined)
            ) {
              shareChange = historyShares[participant][date];
            } else if (
              historyShares[participant][date] !== undefined &&
              historyShares[participant][previousDate] !== undefined
            ) {
              shareChange =
                historyShares[participant][date] -
                historyShares[participant][previousDate];
            }

            sharesChange[participant] = {
              ...sharesChange[participant],
              [date]: shareChange,
            };
          }
        }
      }
      for (const participant in sharesChange) {
        let totalBuy = 0;
        let totalBuyShare = 0;
        let totalSell = 0;
        let totalSellShare = 0;
        for (const date in sharesChange[participant]) {
          const vwap = vwapData[priceDate[priceDate.indexOf(date) - 2]];
          const shareChange = sharesChange[participant][date];
          if (shareChange === 0) {
            continue;
          }
          if (shareChange >= 0) {
            totalBuyShare += shareChange;
            totalBuy += shareChange * vwap;
          } else if (shareChange < 0) {
            const pShareChange = Math.abs(shareChange);
            totalSellShare += pShareChange;
            totalSell += pShareChange * vwap;
          }
        }
        const averages = calculateAverages(
          totalBuy,
          totalBuyShare,
          totalSell,
          totalSellShare
        );
        const totalChangeAmountByParticipant = totalBuy - totalSell;

        totalChangeAmount += totalChangeAmountByParticipant;

        ccassCostData[participant] = {
          ...averages,
          totalChangeAmountByParticipant: Number(
            totalChangeAmountByParticipant.toFixed(2)
          ),
        };
      }
      ccassCostData['totalChangeAmount'] = totalChangeAmount.toFixed(2);
      return ccassCostData;
    },
    [fetchVWAP, fetchHistoryShares, fetchNotInCCASSHistory]
  );
  const fetchHoldCostData = () => {
    return getShareholdingAndVwap(
      queryDataSummary.date,
      compareDataSummary.date
    );
  };
  const updateCompareDateByTradingDay = (days: number) => {
    if (tradingDays.length === 0) return;
    const oldDate = new Date(date);
    const year = oldDate.getFullYear();
    const month = ('0' + (oldDate.getMonth() + 1)).slice(-2);
    const day = ('0' + oldDate.getDate()).slice(-2);
    const formattedDate = year.toString() + month.toString() + day.toString();
    const closetDateIndex = getClosetIndexByDate(tradingDays, formattedDate);
    if (closetDateIndex === null) return;
    const newDate = moment(tradingDays[closetDateIndex - days]).toDate();
    dispatch(uiActions.ccassPage['compareDateChanged'](newDate));
  };
  const { isLoading, data: holdCostData } = useQuery(
    [
      'holdCostData',
      queryDataSummary.date,
      compareDataSummary.date,
      queryDataSummary.ticker,
    ],
    fetchHoldCostData,
    {
      enabled: inView,
    }
  );
  const [dropdownSelect, setDropdownSelect] = useState(0);
  const daysBefore = t('Days Before');
  const dispatch = useDispatch();

  const user = useUser();
  const isAdmin = user && user.isAdmin;

  return (
    <Style>
      <div ref={ref}>
        <LoadingImage loading={isFetching || isLoading} displayAsBlock />
        {data && !isLoading && (
          <>
            <CCASSShareholdingCharts
              querySummary={queryDataSummary}
              queryDetails={queryDetails}
              compareSummary={compareDataSummary}
              compareDetails={compareDetails}
              participants={data.participants}
            />
            <br />
            <Form>
              <Row className="justify-content-md-start">
                {compareDatePicker && (
                  <Form.Group className="form-group" as={Col} md="auto">
                    <Form.Label
                      style={{
                        textAlign: 'center',
                      }}
                    >
                      {' '}
                      <a style={{ display: 'inline' }}>
                        {t('Compare Date')}
                      </a>{' '}
                      {isTablet && (
                        <Dropdown style={{ display: 'inline' }} drop={'down'}>
                          <Dropdown.Toggle id="dropdown-basic">
                            {dropdownSelect
                              ? dropdownSelect + daysBefore + '  '
                              : '選擇'}
                            <FaAngleDown />
                          </Dropdown.Toggle>

                          <Dropdown.Menu>
                            {[
                              ...COMPARE_DAY_DELTA_OPTIONS.map(item => {
                                if (item > tradingDays.length) return null;
                                return (
                                  <Dropdown.Item
                                    key={item}
                                    onClick={() => {
                                      updateCompareDateByTradingDay(item);
                                      setDropdownSelect(item);
                                    }}
                                  >
                                    {item + daysBefore}
                                  </Dropdown.Item>
                                );
                              }),
                              <Dropdown.Item
                                key={'customize'}
                                onClick={() => focusOnCompareDatePicker()}
                              >
                                {t('Customize')}
                              </Dropdown.Item>,
                            ]}
                          </Dropdown.Menu>
                        </Dropdown>
                      )}
                    </Form.Label>
                    <br />
                    {compareDatePicker('compareDate-details')}
                  </Form.Group>
                )}
                {datePicker && (
                  <Form.Group className="form-group" as={Col} md="auto">
                    <Form.Label>{t('Shareholding Date')}</Form.Label>
                    <br />
                    {datePicker()}
                    <div
                      style={{
                        paddingTop: '11px',
                        gap: '8px',
                        display: 'inline-flex',
                        alignItems: 'flex-start',
                      }}
                    >
                      {!isTablet && [
                        ...COMPARE_DAY_DELTA_OPTIONS.map(item => {
                          if (item > tradingDays.length) return null;
                          return (
                            <Button
                              key={item}
                              style={{
                                borderRadius: '100px',
                                background: 'var(--Black-0, #F2F2F2)',
                                color: '#000',
                                padding: '4px 8px',
                                border: '1px solid #F2F2F2',
                              }}
                              onClick={() =>
                                updateCompareDateByTradingDay(item)
                              }
                            >
                              {item + daysBefore}
                            </Button>
                          );
                        }),
                        <Button
                          key={'customize'}
                          style={{
                            borderRadius: '100px',
                            background: 'var(--Black-0, #F2F2F2)',
                            color: '#000',
                            padding: '4px 8px',
                            border: '1px solid #F2F2F2',
                          }}
                          onClick={() => focusOnCompareDatePicker()}
                        >
                          {t('Customize')}
                        </Button>,
                      ]}
                    </div>
                  </Form.Group>
                )}
                {isAdmin && (
                  <Col>
                    <div className="text-right">
                      {dayAfter + t('total change amount by day')}:{' '}
                      {holdCostData['totalChangeAmount']}
                    </div>
                  </Col>
                )}
              </Row>
            </Form>
            <CCASSShareholdingTable
              stockCode={queryDataSummary.ticker}
              queryIssuedShare={queryDataSummary.issued_shares}
              queryTotalShare={queryDataSummary.total_ccass}
              queryDetails={queryDetails}
              compareIssuedShare={compareDataSummary?.issued_shares}
              compareTotalShare={compareDataSummary?.total_ccass}
              compareDetails={compareDetails}
              participants={data.participants}
              holdCostData={holdCostData}
            />
          </>
        )}
      </div>
    </Style>
  );
};

export default CCASSDetails;
